import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateFlagBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateFlag
 *
 * Single endpoint for Add / Edit / Delete on patient flags
 * (whole-state replacement semantic — the body is an ARRAY of
 * `PatientFlag` rows representing the patient's entire flag state).
 * Curl-verified by Andrew 2026-05-20.
 *
 * See `CreateFlagBody` type doc for the per-row semantics. Briefly:
 *   - Existing active row, still selected → ship verbatim
 *   - Existing active → user unselected → `isDeleted: true`
 *   - Existing tombstoned → user re-selected → `isDeleted: false`
 *   - Newly selected → `identifier: null`, `createdBy: currentUser`,
 *     `createdDate: nowIso`, `performerDetails: {}`
 *   - Ghost placeholder rows from GET → pass through verbatim
 *
 * Cache: invalidates `['patient-flags-chart', patientId]`. SignalR
 * `UpdatePatientChat` "Flag" event from PROD-827 Slice 3 also fires
 * for concurrent sessions; the dedup helper short-circuits the
 * SignalR echo of THIS user's own save.
 */

interface CreateFlagParams {
  patientId: string;
  body: CreateFlagBody;
}

export function useCreateFlag() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateFlagParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateFlag', body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-flags-chart', patientId]);
    },
  });
}
